import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { CanActivate } from '@angular/router';
import { isPlatformServer } from '@angular/common';

import { Store, select } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { take, map, switchMap } from 'rxjs/operators';

import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';

import { State } from '../interfaces';
import { getBasketPassengerData, getBookingRef, getUser } from '../store/selectors';
import { AuthActions, BasketActions } from '../store/actions';
import { isAuthenticatedPlusMember } from '../utils/profile.utils';

@Injectable()
export class RefetchGuard implements CanActivate {
  constructor(
    private store$: Store<State>,
    @Inject(PLATFORM_ID) private platform: string
  ) {}

  /**
   * Compares whether the upgrade api call was done when logged in compared to if there is a
   * logged in user profile and vice versa.
   */
  private get loginStatusChanged(): Observable<boolean> {
    return combineLatest([this.store$.pipe(select(getUser)), this.store$.pipe(profileOrUndefinedIfNotLoggedIn())]).pipe(
      /* Check if loggedInState matches  */
      map(([buyer, profile]) => {
        /*
         * Buyer object has info about the user when the upgrade call was made previously,
         * whereas profile object tells about if the user is now logged in or not
         */
        const hadPoints = buyer.points !== undefined;
        const isAuthenticated = isAuthenticatedPlusMember(profile);

        return !((hadPoints && isAuthenticated) || (!hadPoints && !isAuthenticated));
      }),
      take(1)
    );
  }

  canActivate(): true | Observable<boolean> {
    if (isPlatformServer(this.platform)) {
      // Always return true on server so that client handles the requests
      return true;
    }

    return this.loginStatusChanged.pipe(
      switchMap((hasChanged) => {
        if (!hasChanged) {
          return of(true);
        }

        return this.deny();
      })
    );
  }

  private deny(): Observable<boolean> {
    return combineLatest([
      this.store$.pipe(select(getBookingRef)),
      this.store$.pipe(select(getBasketPassengerData)),
    ]).pipe(
      map(([bookingRef, passengers]) => {
        this.store$.dispatch(AuthActions.logout());
        this.store$.dispatch(AuthActions.setBookingRef(bookingRef));

        passengers.forEach((pax) => {
          this.store$.dispatch(BasketActions.addPaxToBasket(pax));
        });

        this.store$.dispatch(AuthActions.loadPNR());

        return false;
      })
    );
  }
}
